import React, { useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';

import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Table,
  Alert,
  InputNumber,
  Icon,
  Spin,
} from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import useAsyncFetch from '../../../../hooks/useAsyncFetch';
import { notify } from '../../../../utils/notify';
import { fetchArchetypes } from '../../../../api/templatesAndTips';
import { setCompassData, setFormData } from '../../actions';
import ModuleSelectModal from '../ModuleSelectModal';
import HubSelectionModal from '../HubSelectionModal';
import FileUploader from '../../../../component/FileUploader';
import { getUploadUrl } from '../../../../utils/getUploadUrl';
import useCreateGlobalListMutation from '../../query/useCreateGlobalListMutation';
import useUpdateGlobalListMutation from '../../query/useUpdateGlobalListMutation';
import {
  autoTrackEvents,
  PATHWAYU_ASSESSMENT_TYPES,
  userTypeOptions,
} from '../../utils';
import useGetGlobalListGoalsQuery from '../../query/useGetGlobalListGoalsQuery';

const { Option } = Select;

const CreateAchievement = ({ form, selectedList, setSelectedList, page }) => {
  const dispatch = useDispatch();
  const containerRef = useRef();

  const [logoUrl, setLogoUrl] = useState('');
  const [isFieldChanged, setIsFieldChanged] = useState(false);

  const { data: listGoals, isLoading } = useGetGlobalListGoalsQuery(
    selectedList?.slug
  );

  const { formData, showModuleSelectionModal, showHubSelectionModal } =
    useSelector(({ compassReducer }) => compassReducer);

  const { response: archetypeData, isLoading: archetypeLoading } =
    useAsyncFetch(true, fetchArchetypes, undefined, {
      onError: () => notify(`Couldn't fetch archetypes!`, 'error'),
    });

  const { mutate: createGlobalList, isLoading: isListGoalCreating } =
    useCreateGlobalListMutation({
      onSuccess: data => {
        setSelectedList(data);
      },
    });

  const { mutate: updateGlobalList, isLoading: isUpadingListGoal } =
    useUpdateGlobalListMutation(page);

  useEffect(() => {
    if (listGoals) dispatch(setFormData(listGoals[0]));
  }, [listGoals]);

  useEffect(() => {
    form.resetFields();
    setLogoUrl(formData.badge?.icon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  if (selectedList?.slug && isLoading) return <Spin />;

  const { goalSetting = {} } = form.getFieldsValue();

  const onFinish = e => {
    e.preventDefault();

    form.validateFields((err, values) => {
      if (!err) {
        if (values.goalSetting.autoTrackEvent === 'PathwayUAssessment') {
          // eslint-disable-next-line no-param-reassign
          values.goalSetting.autoTrackEvent = `${values.goalSetting.autoTrackEvent}.${values.goalSetting.assessmentId}`;
          // eslint-disable-next-line no-param-reassign
          values.goalSetting.limit = 1;
          // eslint-disable-next-line no-param-reassign
          delete values.goalSetting.assessmentId;
        }
        if (formData.id) {
          const updatedValues = {
            ...values,
            id: formData.id,
            listId: selectedList.id,
            badge: { id: formData.badge.id, ...values.badge },
          };
          updateGlobalList(updatedValues);
        } else {
          createGlobalList(values);
        }
        setIsFieldChanged(false);
        form.resetFields();
      }
    });
  };

  const getSelectedArchetypes = (archetype = []) => {
    const arr = [];
    (archetypeData.data?.archetype?.options || []).forEach(
      ({ children = [] }) =>
        children.forEach(({ optionId, optionTitle }) => {
          if (archetype.includes(optionId)) arr.push(optionTitle);
        })
    );
    return arr.join(', ');
  };

  const makeFinalConditionsPreview = () => {
    const { archetypes, userTypes } = form.getFieldsValue()?.goalSetting || {};

    return (
      <>
        <h3>Selected Conditions:</h3>
        <ul>
          {Array.isArray(archetypes) && !isEmpty(archetypes) && (
            <li>
              <strong>Archetype:</strong> {getSelectedArchetypes(archetypes)}
            </li>
          )}
          {Array.isArray(userTypes) && !isEmpty(userTypes) && (
            <li>
              <strong>UserType:</strong> {userTypes.join(', ')}
            </li>
          )}
        </ul>
      </>
    );
  };

  const handleShowModuleModal = () => {
    dispatch(setCompassData({ showModuleSelectionModal: true }));
  };

  const handleShowHubSelectionModal = () => {
    dispatch(setCompassData({ showHubSelectionModal: true }));
  };

  const renderTextRow = (text, record, index) => (
    <div key={index} className="list__table-box">
      {text}
    </div>
  );

  const getModuleColumns = () => [
    {
      title: 'Required Modules',
      dataIndex: 'modules',
      key: 'modules',
      render: renderTextRow,
    },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'id',
      render: (text, record, index) => (
        <div key={index} className="list__table-box">
          <Button type="link" onClick={handleShowModuleModal}>
            View all
          </Button>
        </div>
      ),
    },
  ];

  const getModuleColumnData = () => {
    const { requiredModules } = form.getFieldsValue()?.goalSetting || {};
    if (requiredModules && requiredModules.length) {
      return [{ modules: requiredModules.length, id: 1, key: 1 }];
    }

    return [];
  };

  const getHubSelectionColumns = () => [
    {
      title: 'Active Launch Groups',
      dataIndex: 'groups',
      key: 'groups',
      render: renderTextRow,
    },
    {
      title: 'Active Hubs',
      dataIndex: 'hubs',
      key: 'hubs',
      render: renderTextRow,
    },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'id',
      render: (text, record, index) => (
        <div key={index} className="list__table-box">
          <Button type="link" onClick={handleShowHubSelectionModal}>
            Edit
          </Button>
        </div>
      ),
    },
  ];

  const getHubSelectionColumnData = () => {
    const { launchIn } = form.getFieldsValue()?.goalSetting || {};
    if (launchIn) {
      const { selectedGroups, selectedHubs } = launchIn;
      return [
        {
          groups: selectedGroups && selectedGroups.length,
          hubs: selectedHubs && selectedHubs.length,
          id: 1,
          key: 1,
        },
      ];
    }
    return [];
  };

  const handleUpload = ({ filesUploaded }) => {
    const fileUploadedUrl = getUploadUrl(filesUploaded[0], '/badges/');
    setTimeout(() => setLogoUrl(fileUploadedUrl), 1000);
    form.setFieldsValue({ badge: { icon: fileUploadedUrl } });
    setIsFieldChanged(true);
  };

  const previewAutoTrackLimitMsg = () => {
    const { autoTrackEvent, limit } = form.getFieldsValue()?.goalSetting || {};

    if (autoTrackEvent && limit) {
      const selectedEvent = autoTrackEvents.find(
        event => event.type === autoTrackEvent
      );
      return (
        <Alert
          style={{ marginBottom: 24 }}
          type="info"
          message={
            <div>
              <span>Triggered when the </span>
              <strong>{selectedEvent?.name}</strong>
              <span> by the user is greater than or equal to </span>
              <strong>{limit || `the number provided above`}</strong>
            </div>
          }
        />
      );
    }
    return null;
  };

  return (
    // eslint-disable-next-line no-return-assign
    <div ref={el => (containerRef.current = el)}>
      <Card>
        <h3>{formData.id ? 'Update' : 'Create'} Achievement Form</h3>
        <Form onSubmit={onFinish}>
          <Form.Item label="Badge Name">
            {form.getFieldDecorator('title', {
              rules: [
                { required: true, message: 'Please fill Badge Name!' },
                { max: 20, message: 'Name should be less than 20 character' },
              ],
              initialValue: formData.title,
            })(<Input />)}
          </Form.Item>
          <Form.Item label="Badge Icon" className="ant-form-item">
            {form.getFieldDecorator('badge.icon', {
              rules: [{ required: true, message: 'Please attach icon' }],
              initialValue: formData.badge?.icon,
            })(
              <>
                <div>
                  <span>
                    {`Upload file for Badge Design. We recommend using an image of
                  equal length and width`}
                  </span>
                </div>
                <Input
                  type="text"
                  placeholder="Click to Upload Image"
                  disabled
                  value={logoUrl}
                  addonAfter={
                    <FileUploader
                      type="image"
                      path="/badges/"
                      render={({ onPick }) => (
                        <Icon type="upload" onClick={onPick} />
                      )}
                      onSuccess={handleUpload}
                      options={{ maxFiles: 1 }}
                    />
                  }
                />
                {logoUrl ? (
                  <div className="badge-icon-container">
                    <strong>Preview:</strong>
                    <img
                      src={logoUrl}
                      alt="badge-icon"
                      className="badge-icon-preview"
                      loading="lazy"
                    />
                  </div>
                ) : null}
              </>
            )}
          </Form.Item>
          <Form.Item label="Badge Description">
            {form.getFieldDecorator('information', {
              rules: [
                {
                  required: true,
                  message: 'Please fill Badge description!',
                },
              ],
              initialValue: formData.information,
            })(<Input />)}
          </Form.Item>

          <div
            style={{
              display: 'flex',
              gap: '24px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Form.Item label="Select AutoTrack Event">
              {form.getFieldDecorator('goalSetting.autoTrackEvent', {
                rules: [
                  { required: true, message: 'Please select AutoTrack Event' },
                ],
                initialValue:
                  formData.goalSetting?.autoTrackEvent?.split('.')[0],
              })(
                <Select
                  allowClear
                  getPopupContainer={() => containerRef.current}
                  style={{ minWidth: '300px' }}
                  disabled={!!formData.id}
                >
                  {autoTrackEvents.map(event => (
                    <Option key={event.type} value={event.type}>
                      {event.name}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
            {form.getFieldValue('goalSetting.autoTrackEvent') !==
              'PathwayUAssessment' &&
              form.getFieldValue('goalSetting.autoTrackEvent') && (
                <>
                  <strong style={{ marginTop: '10px' }}>{'>='}</strong>
                  <Form.Item label="Select AutoTrack Event Limit">
                    {form.getFieldDecorator('goalSetting.limit', {
                      rules: [
                        {
                          required: true,
                          message: 'Please select Auto Track Event Limit',
                        },
                      ],
                      initialValue: formData.goalSetting?.limit,
                    })(<InputNumber min={1} disabled={!!formData.id} />)}
                  </Form.Item>
                </>
              )}
            {form.getFieldValue('goalSetting.autoTrackEvent') ===
              'PathwayUAssessment' && (
              <>
                <Form.Item label="Select PathwayU Assessment Type">
                  {form.getFieldDecorator('goalSetting.assessmentId', {
                    rules: [
                      {
                        required: true,
                        message: 'Please select PathwayU Assessment Type',
                      },
                    ],
                    initialValue:
                      formData.goalSetting?.autoTrackEvent?.split('.')[1],
                  })(
                    <Select
                      allowClear
                      getPopupContainer={() => containerRef.current}
                      disabled={!!formData.id}
                    >
                      {Object.values(PATHWAYU_ASSESSMENT_TYPES).map(
                        ({ title, assessmentId }) => (
                          <Option key={assessmentId} value={assessmentId}>
                            {title}
                          </Option>
                        )
                      )}
                    </Select>
                  )}
                </Form.Item>
              </>
            )}
          </div>
          {previewAutoTrackLimitMsg()}
          <Form.Item label="Select archetype">
            {form.getFieldDecorator('goalSetting.archetypes', {
              initialValue: formData.goalSetting?.archetypes,
              rules: [{ required: true, message: 'Please select archetype' }],
            })(
              <Select
                mode="multiple"
                getPopupContainer={() => containerRef.current}
                allowClear
                loading={archetypeLoading}
                disabled={archetypeLoading || selectedList?.active}
                optionFilterProp="title"
              >
                {(archetypeData.data?.archetype?.options || []).reduce(
                  (options, option) => {
                    option.children.forEach(({ optionId, optionTitle }) => {
                      options.push(
                        <Option
                          key={optionId}
                          value={optionId}
                          title={optionTitle}
                        >
                          {optionTitle}
                        </Option>
                      );
                    });

                    return options;
                  },
                  []
                )}
              </Select>
            )}
          </Form.Item>

          <Form.Item label="Select usertypes">
            {form.getFieldDecorator('goalSetting.userTypes', {
              initialValue: formData.goalSetting?.userTypes,
              rules: [{ required: true, message: 'Please select Usertypes' }],
            })(
              <Select
                mode="multiple"
                getPopupContainer={() => containerRef.current}
                allowClear
                disabled={selectedList?.active}
              >
                {userTypeOptions.map(({ value, name }) => (
                  <Option key={value} value={value}>
                    {name}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          {/* <Form.Item label="Click Action">
            {form.getFieldDecorator('goalSetting.clickAction', {
              initialValue: formData.goalSetting?.clickAction,
            })(
              <Select
                mode="default"
                getPopupContainer={() => containerRef.current}
                allowClear
              >
                {clickActionOptions.map(option => (
                  <Option key={option} value={option}>
                    {option}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item> */}
          <Form.Item>
            {form.getFieldDecorator('goalSetting.requiredModules', {
              initialValue: formData.goalSetting?.requiredModules,
            })(
              <Card
                title="Require Module(s)"
                className="form__card"
                extra={
                  <div className="form__card-extra">
                    <Button onClick={handleShowModuleModal}>Select</Button>
                  </div>
                }
                bordered={false}
              >
                <p>
                  Select any modules that are necessary for the badge to earn.
                  Any hubs that don&apos;t have these modules enabled will be
                  automatically excluded from the badge.
                </p>
                <div className="form__card-table">
                  <Table
                    locale={{ emptyText: 'No Module Selected!' }}
                    columns={getModuleColumns()}
                    dataSource={getModuleColumnData()}
                    pagination={false}
                    rowkey="modules"
                  />
                </div>
              </Card>
            )}
          </Form.Item>
          <Form.Item>
            {form.getFieldDecorator('goalSetting.launchIn', {
              initialValue: formData.goalSetting?.launchIn,
            })(
              <Card
                title="Select Hubs"
                className="form__card"
                extra={
                  <div className="form__card-extra">
                    <Button onClick={handleShowHubSelectionModal}>
                      Select
                    </Button>
                  </div>
                }
                bordered={false}
              >
                <p>
                  Select the hubs or launch groups to which you&apos;d like to
                  enable this badge.
                </p>
                <div className="form__card-table">
                  <Table
                    locale={{ emptyText: 'Not hub Selected!' }}
                    columns={getHubSelectionColumns()}
                    dataSource={getHubSelectionColumnData()}
                    pagination={false}
                    rowkey="launch"
                  />
                </div>
              </Card>
            )}
          </Form.Item>
          <Alert
            style={{ marginBottom: 24 }}
            type="info"
            message={makeFinalConditionsPreview()}
          />
          <Form.Item className="templates-and-tips-form-footer">
            <Button
              loading={isListGoalCreating || isUpadingListGoal}
              htmlType="submit"
              type="primary"
              disabled={!(form.isFieldsTouched() || isFieldChanged)}
            >
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Card>
      {showModuleSelectionModal ? (
        <ModuleSelectModal
          modules={goalSetting?.requiredModules || []}
          onSave={data => {
            form.setFieldsValue({ goalSetting: { requiredModules: data } });
            setIsFieldChanged(true);
          }}
        />
      ) : null}

      {showHubSelectionModal ? (
        <HubSelectionModal
          launchIn={goalSetting?.launchIn}
          saveLaunchIn={data => {
            form.setFieldsValue({ goalSetting: { launchIn: data } });
            setIsFieldChanged(true);
          }}
        />
      ) : null}
    </div>
  );
};

export default Form.create({ name: ' create global achievements' })(
  CreateAchievement
);
